import { DestroyRef, ErrorHandler, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { SentryLogger } from '@lentau/core/sentry/shared/services/sentry-logger.service';
import { AppErrorAnalyticsService } from '@lentau/analytics/events/shared/services/app-error-analytics.service';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { ChunkLoadRecoveryService } from '@lentau/core/runtime-recovery/application/recovery/chunk-load-recovery.service';
import { isChunkLoadError } from '@lentau/core/runtime-recovery/shared/chunk-load-error.utils';
import { normalizeHandleableError } from '@lentau/core/sentry/shared/utils/handleable-error-message.utils';
import { NormalizedHandleableError } from '@lentau/core/sentry/shared/utils/handleable-error-message.types';
import { isReportableErrorValue } from '@lentau/core/sentry/shared/utils/reportable-error-value.utils';
import { isExpectedBusinessSentryError } from '@lentau/core/sentry/shared/utils/expected-business-sentry-error.utils';
import { ErrorLevel } from '@lentau/core/sentry/shared/services/sentry.level.const';

@Injectable({
  providedIn: 'root',
})
export class AppErrorHandler implements ErrorHandler {
  private readonly sentryLogger: SentryLogger = inject(SentryLogger);
  private readonly appErrorAnalyticsService: AppErrorAnalyticsService = inject(AppErrorAnalyticsService);
  private readonly browserApiSupportService: BrowserApiSupportService = inject(BrowserApiSupportService);
  private readonly chunkLoadRecoveryService: ChunkLoadRecoveryService = inject(ChunkLoadRecoveryService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly renderer: Renderer2 | null = this.createRenderer();
  private readonly globalErrorListenerUnsubscribers: Array<() => void> = [];
  private readonly handledObjectErrors: WeakSet<object> = new WeakSet<object>();

  constructor() {
    this.setupGlobalErrorListeners();
    this.destroyRef.onDestroy(() => this.teardownGlobalErrorListeners());
  }

  private setupGlobalErrorListeners(): void {
    if (this.browserApiSupportService.isServer() || !this.renderer) {
      return;
    }

    const unsubscribeError: () => void = this.renderer.listen('window', 'error', (event: Event | ErrorEvent) => {
      const errorEvent: ErrorEvent | null = event instanceof ErrorEvent ? event : null;
      this.handleError(errorEvent?.error ?? event);
    });

    const unsubscribeUnhandledRejection: () => void = this.renderer.listen(
      'window',
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        this.handleError(event.reason);
      }
    );

    this.globalErrorListenerUnsubscribers.push(unsubscribeError, unsubscribeUnhandledRejection);
  }

  public handleError(error: unknown): void {
    if (!isReportableErrorValue(error)) {
      return;
    }

    if (typeof error === 'object' && error !== null) {
      if (this.handledObjectErrors.has(error)) {
        return;
      }

      this.handledObjectErrors.add(error);
    }

    if (isChunkLoadError(error)) {
      this.chunkLoadRecoveryService.tryRecover(error);
      return;
    }

    const normalized: NormalizedHandleableError = normalizeHandleableError(error);
    const level: ErrorLevel = isExpectedBusinessSentryError(error) ? ErrorLevel.WARNING : ErrorLevel.ERROR;

    void this.sentryLogger.handleError(normalized, level).catch(() => undefined);

    if (level === ErrorLevel.ERROR) {
      this.appErrorAnalyticsService.trackAppCrash(normalized.reportableError);
    }

    this.chunkLoadRecoveryService.tryRecover(error);
  }

  private teardownGlobalErrorListeners(): void {
    for (const unsubscribe of this.globalErrorListenerUnsubscribers) {
      unsubscribe();
    }

    this.globalErrorListenerUnsubscribers.length = 0;
  }

  private createRenderer(): Renderer2 | null {
    if (this.browserApiSupportService.isServer()) {
      return null;
    }

    return this.rendererFactory.createRenderer(null, null);
  }
}
